/* eslint-disable no-unused-vars */
/* eslint-disable react/display-name */
import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { FormControl, InputBase } from '@material-ui/core';
import NumberFormat from 'react-number-format';
import {
  fontColors,
  backgroundColors,
  colorDanger,
} from '../../material-ui-theme';

// Form value pickup
// component: 'numeric-input'
// value: value

function NumberFormatCustom(props) {
  const { inputRef, onChange, allowNegative, ...other } = props;

  return (
    <NumberFormat
      {...other}
      getInputRef={inputRef}
      onValueChange={(values) => {
        onChange({
          target: {
            value: values.floatValue,
          },
        });
      }}
      allowNegative={allowNegative}
      isNumericString
    />
  );
}

const useStyles = makeStyles((theme) => ({
  inputRoot: {
    color: 'var(--color-fontColors-black)',
  },
  inputPaper: {
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    width: '100%',
    backgroundColor: 'var(--color-white)',
    borderRadius: 4,
    '& input': {
      padding: '6px 3px 6px 6px',
      margin: 0,
      fontSize: '0.9rem',
      fontWeight: 400,
      color: 'var(--color-fontColors-black)',
      border: '1px solid',
      borderRadius: 4,
      borderColor: 'var(--color-colorDefault-main)',
      height: 30,
      minWidth: 40,
      boxSizing: 'border-box',
    },
  },
  inputPaperDisabled: {
    backgroundColor: backgroundColors.disabled,
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    width: '100%',
    borderRadius: 4,
    '& input': {
      padding: '6px 3px 6px 6px',
      margin: 0,
      fontSize: '0.9rem',
      fontWeight: 400,
      color: fontColors.disabled,
      '-webkit-text-fill-color': fontColors.disabled,
      opacity: 1 /* required on iOS */,
      border: '1px solid',
      borderRadius: 4,
      borderColor: 'var(--color-backgroundColors-grey)',
      height: 30,
      minWidth: 40,
      boxSizing: 'border-box',
    },
  },
  helperText: {
    margin: 0,
    fontSize: '0.9rem',
    marginTop: '3px',
    textAlign: 'left',
    fontWeight: 500,
    color: colorDanger.main,
    paddingLeft: theme.spacing(1),
  },
}));

const FlatNumericInput = memo(
  ({
    label,
    onChange,
    error,
    helperText,
    disabled,
    decimalScale,
    suffix,
    prefix,
    hideValueOnDisabled,
    allowNegative,
    inputSize,
    isAllowed,
    ...restProps
  }) => {
    const classes = useStyles();
    const inputProps = {
      decimalScale,
      isAllowed,
      'data-component': 'numeric-input',
    };

    if (suffix !== '') {
      inputProps.suffix = suffix;
      inputProps['data-suffix'] = suffix;
    }

    if (prefix !== '') {
      inputProps.prefix = prefix;
      inputProps['data-prefix'] = prefix;
    }

    if (hideValueOnDisabled) {
      inputProps['data-hidevalueondisabled'] = true;
    }

    inputProps.allowNegative = allowNegative;
    inputProps.size = inputSize;
    inputProps.spellCheck = 'false';

    return (
      <FormControl className={classes.inputRoot} error={error}>
        <div
          className={
            disabled
              ? `${classes.inputPaperDisabled} inputPaperDisabled-class`
              : classes.inputPaper
          }
          elevation={0}
        >
          <InputBase
            onChange={(event) => onChange(event.target.value)}
            className={classes.input}
            inputComponent={NumberFormatCustom}
            inputProps={inputProps}
            disabled={disabled}
            {...restProps}
          />
        </div>
        {error && helperText !== '' ? (
          <p className={classes.helperText}>{helperText}</p>
        ) : null}
      </FormControl>
    );
  }
);

FlatNumericInput.defaultProps = {
  label: '',
  onChange: () => {},
  error: false,
  helperText: '',
  disabled: false,
  decimalScale: 0,
  suffix: '',
  prefix: '',
  hideValueOnDisabled: false,
  allowNegative: false,
  inputSize: 5,
};

FlatNumericInput.propTypes = {
  label: PropTypes.string,
  onChange: PropTypes.func,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  disabled: PropTypes.bool,
  decimalScale: PropTypes.number,
  suffix: PropTypes.string,
  prefix: PropTypes.string,
  hideValueOnDisabled: PropTypes.bool,
  allowNegative: PropTypes.bool,
  inputSize: PropTypes.number,
};

export default FlatNumericInput;
